import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Upload,
  Package,
  DollarSign,
  CheckCircle
} from 'lucide-react';
import { ProductService } from '../../services/productService';
import { CategoryService } from '../../services/categoryService';
import { TopupTemplateService } from '../../services/topupTemplateService';
import LoadingSpinner from '../common/LoadingSpinner';
import { useNotification } from '../common/Notification';

const RESERVED_TOPUP_SERVICES = ['free fire'];
const normalizeValue = (value = '') => String(value || '').trim().toLowerCase();
const CARD_REGION_OPTIONS = ['DZ', 'FR', 'EU', 'US', 'UK', 'ES', 'DE', 'IT', 'GLOBAL'];

const inferTopupUnit = (offer = {}) => {
  const explicitUnit = String(offer.unit || '').trim();
  if (explicitUnit) {
    return explicitUnit;
  }

  const sourceText = String(offer.value || offer.name || '').trim();
  if (!sourceText) {
    return '';
  }

  const trailingUnitMatch = sourceText.match(/([A-Za-zÀ-ÿ][A-Za-zÀ-ÿ\s]{1,24})$/u);
  return trailingUnitMatch ? trailingUnitMatch[1].trim() : '';
};

const NewProduct = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addNotification } = useNotification();
  const requestedSection = useMemo(() => {
    if (location.pathname === '/admin/topup/services/new') {
      return CategoryService.CATEGORY_SECTIONS.TOPUP;
    }

    const params = new URLSearchParams(location.search);
    return params.get('section') === CategoryService.CATEGORY_SECTIONS.TOPUP
      ? CategoryService.CATEGORY_SECTIONS.TOPUP
      : CategoryService.CATEGORY_SECTIONS.CARDS;
  }, [location.pathname, location.search]);
  
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [topupTemplates, setTopupTemplates] = useState([]);
  const [existingProducts, setExistingProducts] = useState([]);
  const [isCustomGame, setIsCustomGame] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    game: '',
    category: '',
    categoryId: '',
    subcategoryId: '',
    subcategoryName: '',
    section: requestedSection,
    description: '',
    imageUrl: '',
    coverImageUrl: '',
    detailImageUrl: '',
    unitIconUrl: '',
    status: 'active',
    topupTemplateId: '',
    topupTemplateName: '',
    topupTemplateFamily: '',
    topupFulfillmentMode: ''
  });
  
  const [offers, setOffers] = useState([
    {
      id: Date.now(),
      name: '',
      value: '',
      unit: '',
      price: '',
      region: 'DZ',
      stock: 0,
      codes: []
    }
  ]);
  
  const [errors, setErrors] = useState({});
  const [showCodeImport, setShowCodeImport] = useState(null);
  const [importText, setImportText] = useState('');

  const createEmptyOffer = () => ({
    id: Date.now(),
    name: '',
    value: '',
    unit: '',
    price: '',
    region: 'DZ',
    stock: 0,
    codes: []
  });

  // Jeux populaires avec leurs variantes communes
  const gameTemplates = {
    'Free Fire': [
      { name: '100 + 10 Diamants', value: '110', unit: 'Diamants', price: 150 },
      { name: '200 + 20 Diamants', value: '220', unit: 'Diamants', price: 300 },
      { name: '500 + 50 Diamants', value: '550', unit: 'Diamants', price: 750 },
      { name: '1000 + 100 Diamants', value: '1100', unit: 'Diamants', price: 1500 },
      { name: '2000 + 200 Diamants', value: '2200', unit: 'Diamants', price: 3000 }
    ],
    'PUBG Mobile': [
      { name: '60 UC', value: '60', unit: 'UC', price: 120 },
      { name: '300 + 25 UC', value: '325', unit: 'UC', price: 600 },
      { name: '600 + 60 UC', value: '660', unit: 'UC', price: 1200 },
      { name: '1500 + 300 UC', value: '1800', unit: 'UC', price: 3000 },
      { name: '3000 + 850 UC', value: '3850', unit: 'UC', price: 6000 }
    ],
    'Mobile Legends': [
      { name: '86 Diamants', value: '86', unit: 'Diamants', price: 150 },
      { name: '172 Diamants', value: '172', unit: 'Diamants', price: 300 },
      { name: '429 + 71 Diamants', value: '500', unit: 'Diamants', price: 750 },
      { name: '878 + 122 Diamants', value: '1000', unit: 'Diamants', price: 1500 },
      { name: '1757 + 343 Diamants', value: '2100', unit: 'Diamants', price: 3000 }
    ],
    'Roblox': [
      { name: '80 Robux', value: '80', unit: 'Robux', price: 120 },
      { name: '400 Robux', value: '400', unit: 'Robux', price: 600 },
      { name: '800 Robux', value: '800', unit: 'Robux', price: 1200 },
      { name: '1700 Robux', value: '1700', unit: 'Robux', price: 2400 },
      { name: '4500 Robux', value: '4500', unit: 'Robux', price: 6000 }
    ]
  };

  // Charger les catégories au montage du composant
  useEffect(() => {
    const loadContext = async () => {
      try {
        const [categoriesData, templatesData, productsData] = await Promise.all([
          CategoryService.getActiveCategories(),
          TopupTemplateService.getActiveTemplates(),
          ProductService.getAllProducts()
        ]);
        setCategories(categoriesData);
        setTopupTemplates(templatesData);
        setExistingProducts(productsData);
      } catch (error) {
        console.error('Erreur lors du chargement du workflow produit:', error);
        addNotification('error', 'Erreur de chargement', 'Impossible de charger les categories et templates');
      }
    };

    loadContext();
  }, [addNotification]);

  useEffect(() => {
    setFormData((current) => ({ ...current, section: requestedSection }));
  }, [requestedSection]);

  const visibleCategories = useMemo(() => {
    if (requestedSection === CategoryService.CATEGORY_SECTIONS.TOPUP) {
      return categories.filter((category) => CategoryService.isTopupParentCategory(category));
    }

    return categories;
  }, [categories, requestedSection]);

  const selectedCategory = useMemo(() => {
    return categories.find((category) => category.id === formData.categoryId) || null;
  }, [categories, formData.categoryId]);

  const isTopupService = selectedCategory
    ? CategoryService.matchesSection(selectedCategory, CategoryService.CATEGORY_SECTIONS.TOPUP)
    : formData.section === CategoryService.CATEGORY_SECTIONS.TOPUP;

  const visibleSubcategories = useMemo(() => {
    if (!isTopupService || !formData.categoryId) {
      return [];
    }

    return categories.filter((category) => (
      CategoryService.isTopupSubcategory(category) &&
      String(category.parentCategoryId || '').trim() === String(formData.categoryId || '').trim()
    ));
  }, [categories, formData.categoryId, isTopupService]);

  const selectedSubcategory = useMemo(() => {
    return categories.find((category) => category.id === formData.subcategoryId) || null;
  }, [categories, formData.subcategoryId]);

  const duplicateTopupServices = useMemo(() => {
    if (!isTopupService) {
      return [];
    }

    const normalizedName = normalizeValue(formData.name);
    const normalizedGame = normalizeValue(formData.game);
    const normalizedCategoryId = formData.categoryId;
    const normalizedSubcategoryId = formData.subcategoryId;

    if (!normalizedName && !normalizedGame) {
      return [];
    }

    const duplicates = existingProducts.filter((product) => {
      const productSection = product.section ||
        (selectedCategory && product.categoryId === selectedCategory.id
          ? selectedCategory.section
          : null);

      if (productSection !== CategoryService.CATEGORY_SECTIONS.TOPUP) {
        return false;
      }

      const sameCategory = normalizedCategoryId
        ? product.categoryId === normalizedCategoryId
        : true;

      if (!sameCategory) {
        return false;
      }

      const sameSubcategory = normalizedSubcategoryId
        ? product.subcategoryId === normalizedSubcategoryId
        : !product.subcategoryId;

      if (!sameSubcategory) {
        return false;
      }

      const productName = normalizeValue(product.name);
      const productGame = normalizeValue(product.game);
      return (
        (normalizedName && productName === normalizedName) ||
        (normalizedGame && productGame === normalizedGame)
      );
    });

    const isReservedService = RESERVED_TOPUP_SERVICES.some((reserved) => (
      reserved === normalizedName || reserved === normalizedGame
    ));

    if (!isReservedService) {
      return duplicates;
    }

    return [
      ...duplicates,
      {
        id: 'reserved_free_fire',
        name: 'Free Fire',
        game: 'Free Fire',
        reserved: true
      }
    ];
  }, [existingProducts, formData.categoryId, formData.game, formData.name, formData.subcategoryId, isTopupService, selectedCategory]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Effacer l'erreur si elle existe
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleGameChange = (game) => {
    if (game === 'autre') {
      setIsCustomGame(true);
      setFormData(prev => ({ ...prev, game: '' }));
      setOffers([createEmptyOffer()]);
      return;
    }

    setIsCustomGame(false);
    setFormData(prev => ({ ...prev, game }));
    
    // Proposer les variantes communes pour ce jeu
    if (gameTemplates[game]) {
      const templates = gameTemplates[game].map((template, index) => ({
        id: Date.now() + index * 100, // Assurer des IDs uniques
        name: template.name,
        value: template.value,
        unit: template.unit || inferTopupUnit(template),
        price: template.price,
        region: 'DZ',
        stock: 0,
        codes: []
      }));
      setOffers(templates);
      console.log(`Chargement de ${templates.length} templates pour ${game}:`, templates);
    } else {
      // Si pas de template, garder une offre vide
      setOffers([createEmptyOffer()]);
    }
  };

  const handleCategoryChange = (event) => {
    const categoryId = event.target.value;
    const category = categories.find((item) => item.id === categoryId) || null;
    const isTopupCategory = category
      ? CategoryService.matchesSection(category, CategoryService.CATEGORY_SECTIONS.TOPUP)
      : false;

    setFormData((current) => ({
      ...current,
      categoryId,
      category: category?.name || '',
      subcategoryId: '',
      subcategoryName: '',
      section: category?.section || current.section,
      topupTemplateId: isTopupCategory ? current.topupTemplateId : '',
      topupTemplateName: isTopupCategory ? current.topupTemplateName : '',
      topupTemplateFamily: isTopupCategory ? current.topupTemplateFamily : '',
      topupFulfillmentMode: isTopupCategory ? current.topupFulfillmentMode : ''
    }));

    if (errors.categoryId || errors.category || errors.subcategoryId) {
      setErrors((current) => ({ ...current, categoryId: null, category: null, subcategoryId: null }));
    }
  };

  const handleSubcategoryChange = (event) => {
    const subcategoryId = event.target.value;
    const subcategory = categories.find((item) => item.id === subcategoryId) || null;

    setFormData((current) => ({
      ...current,
      subcategoryId,
      subcategoryName: subcategory?.name || ''
    }));

    if (errors.subcategoryId) {
      setErrors((current) => ({ ...current, subcategoryId: null }));
    }
  };

  const handleTopupTemplateChange = (event) => {
    const templateId = event.target.value;
    const template = topupTemplates.find((item) => item.id === templateId) || null;

    setFormData((current) => ({
      ...current,
      topupTemplateId: templateId,
      topupTemplateName: template?.name || '',
      topupTemplateFamily: template?.family || '',
      topupFulfillmentMode: template?.submissionMode || ''
    }));

    if (errors.topupTemplateId) {
      setErrors((current) => ({ ...current, topupTemplateId: null }));
    }
  };

  const addOffer = () => {
    const newOffer = createEmptyOffer();
    setOffers(prev => [...prev, newOffer]);
  };

  const removeOffer = (offerId) => {
    setOffers(prev => prev.filter(offer => offer.id !== offerId));
  };

  const updateOffer = (offerId, field, value) => {
    setOffers(prev => prev.map(offer => 
      offer.id === offerId ? { ...offer, [field]: value } : offer
    ));
  };

  const handleCodeImport = (offerId) => {
    if (!importText.trim()) {
      addNotification('error', 'Codes manquants', 'Veuillez saisir les codes à importer');
      return;
    }

    const codes = importText
      .split('\n')
      .map(code => code.trim())
      .filter(code => code.length > 0);

    if (codes.length === 0) {
      addNotification('error', 'Codes invalides', 'Aucun code valide trouvé');
      return;
    }

    // Mettre à jour l'offre avec les nouveaux codes
    setOffers(prev => prev.map(offer => {
      if (offer.id === offerId) {
        const existingCodes = new Set((offer.codes || []).map((code) => code.trim()).filter(Boolean));
        const dedupedCodes = [];

        codes.forEach((code) => {
          const normalizedCode = code.trim();
          if (!normalizedCode || existingCodes.has(normalizedCode)) {
            return;
          }

          existingCodes.add(normalizedCode);
          dedupedCodes.push(normalizedCode);
        });

        const newCodes = [...(offer.codes || []), ...dedupedCodes];
        return {
          ...offer,
          codes: newCodes,
          stock: newCodes.length
        };
      }
      return offer;
    }));

    setImportText('');
    setShowCodeImport(null);
    addNotification('success', 'Codes importés', `${codes.length} code(s) traité(s) avec succès`);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom du produit est requis';
    }

    if (!formData.game.trim()) {
      newErrors.game = 'Le jeu est requis';
    }

    if (!formData.categoryId.trim()) {
      newErrors.categoryId = 'La categorie est requise';
    }

    if (isTopupService && !formData.subcategoryId.trim()) {
      newErrors.subcategoryId = 'La sous-categorie Topup est requise';
    }

    if (isTopupService && !formData.topupTemplateId.trim()) {
      newErrors.topupTemplateId = 'Le template Topup est requis';
    }

    if (isTopupService && duplicateTopupServices.length > 0) {
      newErrors.serviceDuplicate = 'Ce service Topup existe deja ou reserve deja un service actif';
    }

    if (offers.length === 0) {
      newErrors.offers = 'Au moins une offre est requise';
    } else {
      offers.forEach((offer, index) => {
        if (!offer.name.trim()) {
          newErrors[`offer_${index}_name`] = 'Le nom de l\'offre est requis';
        }
        if (!offer.value.trim()) {
          newErrors[`offer_${index}_value`] = 'La valeur est requise';
        }
        if (!offer.price || offer.price <= 0) {
          newErrors[`offer_${index}_price`] = 'Le prix doit être supérieur à 0';
        }
        if (isTopupService && !String(offer.unit || '').trim()) {
          newErrors[`offer_${index}_unit`] = 'L unite est requise pour un service Topup';
        }
        if (!isTopupService && !String(offer.region || '').trim()) {
          newErrors[`offer_${index}_region`] = 'La region est requise';
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      addNotification('error', 'Formulaire invalide', 'Veuillez corriger les erreurs dans le formulaire');
      return;
    }

    setLoading(true);
    
    try {
      const productData = {
        name: formData.name.trim(),
        game: formData.game.trim(),
        category: formData.category,
        categoryId: formData.categoryId,
        subcategoryId: isTopupService ? formData.subcategoryId : '',
        subcategoryName: isTopupService ? (selectedSubcategory?.name || formData.subcategoryName || '') : '',
        section: formData.section,
        description: formData.description,
        imageUrl: formData.imageUrl.trim(),
        coverImageUrl: formData.coverImageUrl.trim(),
        detailImageUrl: formData.detailImageUrl.trim(),
        unitIconUrl: formData.unitIconUrl.trim(),
        status: formData.status,
        offers: offers.map(offer => ({
          id: offer.id.toString(),
          name: offer.name,
          value: offer.value,
          ...(isTopupService ? { unit: String(offer.unit || '').trim() } : {}),
          price: parseFloat(offer.price),
          region: offer.region,
          stock: 0
        })),
        ...(isTopupService ? {
          topupTemplateId: formData.topupTemplateId,
          topupTemplateName: formData.topupTemplateName,
          topupTemplateFamily: formData.topupTemplateFamily,
          topupFulfillmentMode: formData.topupFulfillmentMode
        } : {})
      };

      const productId = await ProductService.createProduct(productData);
      
      // Importer les codes pour chaque offre
      for (const offer of offers) {
        if (offer.codes && offer.codes.length > 0) {
          await ProductService.importCodes(productId, offer.id.toString(), offer.codes);
        }
      }

      addNotification('success', isTopupService ? 'Service Topup cree' : 'Produit cree', isTopupService ? 'Le service Topup a ete cree avec succes' : 'Produit cree avec succes');
      navigate(isTopupService ? '/admin/topup/services' : '/admin/products');
    } catch (error) {
      console.error('Erreur lors de la création du produit:', error);
      addNotification('error', 'Erreur de création', 'Erreur lors de la création du produit');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Link
              to={requestedSection === CategoryService.CATEGORY_SECTIONS.TOPUP ? '/admin/topup/services' : '/admin/products'}
              className="mr-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
              <h1 className="text-2xl font-bold text-gray-900">
                {requestedSection === CategoryService.CATEGORY_SECTIONS.TOPUP ? 'Nouveau service Topup' : 'Nouveau produit'}
            </h1>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
        {/* Informations générales */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Package className="w-5 h-5 mr-2" />
            Informations générales
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom du produit *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.name ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Ex: Diamants Free Fire"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Jeu *
              </label>
              <select
                name="game"
                value={isCustomGame ? 'autre' : formData.game}
                onChange={(e) => {
                  console.log('Jeu sélectionné:', e.target.value);
                  handleGameChange(e.target.value);
                }}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.game ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Sélectionner un jeu</option>
                {Object.keys(gameTemplates).map(game => (
                  <option key={game} value={game}>{game}</option>
                ))}
                <option value="autre">Autre (personnalisé)</option>
              </select>
              {isCustomGame && (
                <input
                  type="text"
                  name="game"
                  value={formData.game}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent mt-2"
                  placeholder="Nom du jeu personnalisé"
                />
              )}
              {errors.game && (
                <p className="mt-1 text-sm text-red-600">{errors.game}</p>
              )}
              {formData.game && gameTemplates[formData.game] && (
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <strong>✓ Templates chargés:</strong> {gameTemplates[formData.game].length} variantes pour {formData.game}
                  </p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Categorie *
              </label>
              <select
                value={formData.categoryId}
                onChange={handleCategoryChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              >
                <option value="">Selectionner une categorie</option>
                {visibleCategories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}{requestedSection !== CategoryService.CATEGORY_SECTIONS.TOPUP ? ` - ${category.section === CategoryService.CATEGORY_SECTIONS.TOPUP ? 'Topup' : 'Cartes'}` : ''}
                  </option>
                ))}
              </select>
              {errors.categoryId && (
                <p className="mt-1 text-sm text-red-600">{errors.categoryId}</p>
              )}
              {visibleCategories.length === 0 && (
                <p className="mt-1 text-sm text-amber-600">
                  Aucune catégorie disponible. 
                  <Link to={`/admin/categories${requestedSection === CategoryService.CATEGORY_SECTIONS.TOPUP ? '' : ''}`} className="text-primary-600 hover:text-primary-700 ml-1">
                    Creer une categorie
                  </Link>
                </p>
              )}
            </div>

            {isTopupService ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sous-categorie Topup *
                </label>
                <select
                  value={formData.subcategoryId}
                  onChange={handleSubcategoryChange}
                  disabled={!formData.categoryId}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.subcategoryId ? 'border-red-300' : 'border-gray-300'
                  } disabled:bg-gray-100 disabled:text-gray-500`}
                >
                  <option value="">
                    {formData.categoryId ? 'Selectionner une sous-categorie' : 'Choisissez d abord une categorie'}
                  </option>
                  {visibleSubcategories.map((subcategory) => (
                    <option key={subcategory.id} value={subcategory.id}>{subcategory.name}</option>
                  ))}
                </select>
                {errors.subcategoryId ? (
                  <p className="mt-1 text-sm text-red-600">{errors.subcategoryId}</p>
                ) : null}
                {formData.categoryId && visibleSubcategories.length === 0 ? (
                  <p className="mt-1 text-sm text-amber-600">
                    Aucune sous-categorie active pour cette categorie. Cree-la d abord dans la gestion des categories Topup.
                  </p>
                ) : null}
              </div>
            ) : null}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
              URL image par defaut
              </label>
              <input
                type="url"
                name="imageUrl"
                value={formData.imageUrl}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="https://exemple.com/image.jpg"
              />
              <p className="mt-1 text-xs text-gray-500">
                Utilisee en secours si l image cover ou detail n est pas renseignee.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL image cover liste
              </label>
              <input
                type="url"
                name="coverImageUrl"
                value={formData.coverImageUrl}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder={isTopupService ? 'https://exemple.com/cover-topup.jpg' : 'https://exemple.com/cover-card.jpg'}
              />
              <p className="mt-1 text-xs text-gray-500">
                {isTopupService
                  ? 'Format recommande: rectangulaire pour la carte service Topup.'
                  : 'Format recommande: image de liste pour les jeux et produits cartes.'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL image detail produit
              </label>
              <input
                type="url"
                name="detailImageUrl"
                value={formData.detailImageUrl}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder={isTopupService ? 'https://exemple.com/detail-topup.jpg' : 'https://exemple.com/detail-card.jpg'}
              />
              <p className="mt-1 text-xs text-gray-500">
                {isTopupService
                  ? 'Format recommande: carre pour la fiche produit Topup.'
                  : 'Format recommande: image dediee a la fiche produit cartes.'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL image unite
              </label>
              <input
                type="url"
                name="unitIconUrl"
                value={formData.unitIconUrl}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder={isTopupService ? 'https://exemple.com/icon-uc.png' : 'https://exemple.com/icon-card.png'}
              />
              <p className="mt-1 text-xs text-gray-500">
                {isTopupService
                  ? 'Optionnel. S affiche a cote de l unite sur les cartes d offres Topup.'
                  : 'Optionnel. S affiche sur les cartes d offres du catalogue a la place de l image produit reduite.'}
              </p>
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Description du produit..."
            />
          </div>

          <div className="mt-6">
            <div className={`rounded-xl border p-4 ${isTopupService ? 'border-blue-200 bg-blue-50' : 'border-emerald-200 bg-emerald-50'}`}>
              <p className="text-sm font-semibold text-gray-800">
                {isTopupService ? 'Workflow service Topup' : 'Workflow cartes'}
              </p>
              <p className="mt-1 text-sm text-gray-600">
                {isTopupService
                  ? 'Ce service sera visible dans Topup et doit etre relie a un template pour afficher le bon formulaire.'
                  : 'Ce produit reste dans le catalogue cartes avec gestion des codes.'}
              </p>
            </div>
          </div>

          {isTopupService && (
            <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Template Topup *
                </label>
                <select
                  value={formData.topupTemplateId}
                  onChange={handleTopupTemplateChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.topupTemplateId ? 'border-red-300' : 'border-gray-300'
                  }`}
                >
                  <option value="">Selectionner un template Topup</option>
                  {topupTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name} - {TopupTemplateService.getFamilyLabel(template.family)}
                    </option>
                  ))}
                </select>
                {errors.topupTemplateId && (
                  <p className="mt-1 text-sm text-red-600">{errors.topupTemplateId}</p>
                )}
                <p className="mt-2 text-xs text-gray-500">
                  Tu peux creer les templates dans <Link to="/admin/topup/templates" className="text-primary-600 hover:text-primary-700">Templates Topup</Link>.
                </p>
              </div>

              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <p className="text-sm font-semibold text-gray-800">Service deja existant ?</p>
                <p className="mt-1 text-sm text-gray-500">
                  On controle les services deja presents pour eviter les doublons Topup.
                </p>
                {duplicateTopupServices.length === 0 ? (
                  <p className="mt-3 text-sm font-medium text-emerald-600">Aucun doublon detecte.</p>
                ) : (
                  <div className="mt-3 space-y-2">
                    {duplicateTopupServices.map((product) => (
                      <div key={product.id} className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                        <span className="font-semibold">{product.game || product.name}</span>
                        {product.reserved ? ' existe deja comme service reserve' : ' existe deja dans les services Topup'}
                        {!product.reserved && (
                          <Link to={`/admin/products/${product.id}/edit?section=topup`} className="ml-2 font-semibold text-amber-800 underline">
                            Modifier
                          </Link>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {errors.serviceDuplicate && (
                  <p className="mt-2 text-sm text-red-600">{errors.serviceDuplicate}</p>
                )}
              </div>
            </div>
          )}

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Statut
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
              <option value="draft">Brouillon</option>
            </select>
          </div>
        </div>

        {/* Offres et variantes */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <DollarSign className="w-5 h-5 mr-2" />
              Offres et variantes
            </h2>
            <button
              type="button"
              onClick={addOffer}
              className="inline-flex items-center px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter une offre
            </button>
          </div>

          {errors.offers && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{errors.offers}</p>
            </div>
          )}

          <div className="space-y-6">
            {offers.map((offer, index) => (
              <div key={offer.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-md font-medium text-gray-900">
                    Offre #{index + 1}
                  </h3>
                  {offers.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeOffer(offer.id)}
                      className="text-red-600 hover:text-red-700 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div className={`grid grid-cols-1 gap-4 mb-4 ${!isTopupService ? 'md:grid-cols-4' : 'md:grid-cols-4'}`}>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom de l'offre *
                    </label>
                    <input
                      type="text"
                      value={offer.name}
                      onChange={(e) => updateOffer(offer.id, 'name', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors[`offer_${index}_name`] ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Ex: 100 + 10 Diamants"
                    />
                    {errors[`offer_${index}_name`] && (
                      <p className="mt-1 text-sm text-red-600">{errors[`offer_${index}_name`]}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Valeur *
                    </label>
                    <input
                      type="text"
                      value={offer.value}
                      onChange={(e) => updateOffer(offer.id, 'value', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors[`offer_${index}_value`] ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Ex: 110 Diamants"
                    />
                    {errors[`offer_${index}_value`] && (
                      <p className="mt-1 text-sm text-red-600">{errors[`offer_${index}_value`]}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prix (DA) *
                    </label>
                    <input
                      type="number"
                      value={offer.price}
                      onChange={(e) => updateOffer(offer.id, 'price', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors[`offer_${index}_price`] ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="150"
                      min="0"
                      step="0.01"
                    />
                    {errors[`offer_${index}_price`] && (
                      <p className="mt-1 text-sm text-red-600">{errors[`offer_${index}_price`]}</p>
                    )}
                  </div>

                  {isTopupService && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Unite *
                      </label>
                      <input
                        type="text"
                        value={offer.unit || ''}
                        onChange={(e) => updateOffer(offer.id, 'unit', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                          errors[`offer_${index}_unit`] ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="Ex: mois, UC, Coins, Robux"
                      />
                      {errors[`offer_${index}_unit`] && (
                        <p className="mt-1 text-sm text-red-600">{errors[`offer_${index}_unit`]}</p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        Unite affichee cote revendeur pour cette offre.
                      </p>
                    </div>
                  )}

                  {!isTopupService && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Region *
                      </label>
                      <input
                        type="text"
                        list="card-region-options"
                        value={offer.region || ''}
                        onChange={(e) => updateOffer(offer.id, 'region', e.target.value.toUpperCase())}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                          errors[`offer_${index}_region`] ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="Ex: FR"
                      />
                      {errors[`offer_${index}_region`] && (
                        <p className="mt-1 text-sm text-red-600">{errors[`offer_${index}_region`]}</p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        Exemple: `DZ`, `FR`, `EU`, `US`, `GLOBAL`
                      </p>
                    </div>
                  )}
                </div>

                {!isTopupService && (
                <div className="border-t border-gray-100 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-700 mr-3">
                        Codes ({offer.codes?.length || 0})
                      </span>
                      {offer.codes?.length > 0 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          {offer.codes.length} code(s) importé(s)
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowCodeImport(offer.id)}
                      className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Importer codes
                    </button>
                  </div>

                  {showCodeImport === offer.id && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Codes (un par ligne)
                      </label>
                      <textarea
                        value={importText}
                        onChange={(e) => setImportText(e.target.value)}
                        rows={6}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={`CODE1234567890\nCODE0987654321\nCODE1122334455\n...`}
                      />
                      <div className="flex items-center justify-between mt-3">
                        <p className="text-xs text-gray-500">
                          Collez vos codes ici, un par ligne. Les lignes vides seront ignorées.
                        </p>
                        <div className="flex space-x-2">
                          <button
                            type="button"
                            onClick={() => {
                              setShowCodeImport(null);
                              setImportText('');
                            }}
                            className="px-3 py-1 text-gray-600 hover:text-gray-700 transition-colors text-sm"
                          >
                            Annuler
                          </button>
                          <button
                            type="button"
                            onClick={() => handleCodeImport(offer.id)}
                            className="px-3 py-1 bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors text-sm"
                          >
                            Importer
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        {!isTopupService && (
          <datalist id="card-region-options">
            {CARD_REGION_OPTIONS.map((region) => (
              <option key={region} value={region} />
            ))}
          </datalist>
        )}

        <div className="flex items-center justify-end space-x-4">
          <Link
            to={requestedSection === CategoryService.CATEGORY_SECTIONS.TOPUP ? '/admin/topup/services' : '/admin/products'}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Annuler
          </Link>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Création...
              </>
            ) : (
              (isTopupService ? 'Creer le service Topup' : 'Creer le produit')
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewProduct;
